import { Profession, Specialization } from '@bemlo/enums'
import { isNoneSpecialization } from '@bemlo/utils'

import { Badge } from './Badge'
import { Circle } from './Circle'
import { Div } from './Div'
import { Flex } from './Flex'
import { Tooltip } from './Tooltip'
import { Color } from '../constants'
import { useTranslations } from '../translate'

const PROFESSION_COLOR_MAP: Partial<Record<Profession, Color>> = {
  [Profession.ASSISTANT_NURSE]: Color.GREEN,
  [Profession.AUDIOLOGIST]: Color.YELLOW,
  [Profession.CARER]: Color.VIOLET,
  [Profession.DOCTOR]: Color.RED,
  [Profession.NURSE]: Color.BLUE,
  [Profession.PSYCHOTHERAPIST]: Color.PINK,
  [Profession.SENIOR_CARER]: Color.DARK_PURPLE,
}

type EducationBadgeProps = {
  preferredSpecialization?: Specialization
  profession: Profession
  size?: EducationBadgeSize
  specializations: Specialization[]
}

type EducationBadgeSize = 'sm' | 'md'

export const EducationBadge = (props: EducationBadgeProps) => {
  const t = useTranslations()
  const specializations = [...props.specializations]
    .filter((specialization) => !isNoneSpecialization(specialization))
    .sort((a, b) => {
      if (a === props.preferredSpecialization) {
        return -1
      } else if (b === props.preferredSpecialization) {
        return 1
      } else {
        return 0
      }
    })

  const specializationLabel = specializations.length
    ? `${t.$specializationEducation(specializations[0])}${
        specializations.length > 1 ? `, +${specializations.length - 1}` : ''
      }`
    : ''

  return (
    <Flex gap={2}>
      <Tooltip>
        <Tooltip.Content intent="info" side="left">
          {t.$profession(props.profession)}
          {specializations.length > 0 && ', '}
          {specializations
            .map((specialization) => t.$specializationEducation(specialization))
            .join(', ')}
        </Tooltip.Content>
        <Tooltip.Trigger asChild>
          {/* this wrapper is needed for correct tooltip positioning */}
          <Flex gap={2} styles={{ cursor: 'pointer' }}>
            <Badge color="transparent" px="10px" pill size={props.size}>
              <Circle
                size={9}
                backgroundColor={
                  PROFESSION_COLOR_MAP[props.profession] ?? Color.GREY
                }
              />
              {t.$professionShort(props.profession)}
            </Badge>
            {specializations.length > 0 && (
              <Div styles={{ cursor: 'pointer' }}>
                <Badge color="transparent" px="10px" pill>
                  {specializationLabel}
                </Badge>
              </Div>
            )}
          </Flex>
        </Tooltip.Trigger>
      </Tooltip>
    </Flex>
  )
}
